import {
  DurationFilterOptions,
  ID,
  overdueSectionID,
  Task,
  TaskType,
  Timestamp,
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfYear,
  isAfter,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from 'date-fns';

import { formatDateKey } from '../format-date-key';
import { getEffectiveDeadline } from '../get-effective-deadline';
import { getDueTaskPlanning, getTaskPlanning } from '../get-task-planning';
import { someFilterMatchesDuration } from '../matches-duration-filter';

export type UpcomingSection = {
  id: ID;
  startDate: Timestamp;
  endDate: Timestamp;
  isDay: boolean;
};

export type BuildUpcomingDeadlineBucketsOptions = {
  tasks: Task[];
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFilterEndDate?: Timestamp | null;
  shouldHaveRestOfMonth: boolean;
  restOfMonthStartDate: Timestamp;
  // When false (the default), a recurring task's occurrences that are already
  // completed for their date are excluded, so a habit completed today drops out
  // of its day section. When true, completed occurrences are kept.
  showCompleted?: boolean;
  // Applied per-occurrence only; regular tasks are duration-filtered upstream.
  durationFilters?: DurationFilterOptions[];
};

export type UpcomingDeadlineBuckets = {
  sections: UpcomingSection[];
  sectionTasks: Record<ID, Task[]>;
};

const buildSections = (
  today: Timestamp,
  shouldHaveRestOfMonth: boolean,
  restOfMonthStartDate: Timestamp,
  maxDate: Timestamp,
): UpcomingSection[] => {
  const sections: UpcomingSection[] = [];

  sections.push({
    id: overdueSectionID,
    startDate: new Date(0),
    endDate: endOfDay(addDays(today, -1)),
    isDay: true,
  });

  sections.push(
    ...Array.from({ length: 10 }, (_, i) => {
      const day = addDays(today, i);
      return {
        id: formatDateKey(endOfDay(day)),
        startDate: startOfDay(day),
        endDate: endOfDay(day),
        isDay: true,
      };
    }),
  );

  if (shouldHaveRestOfMonth) {
    const romStart = startOfDay(restOfMonthStartDate);
    const romEnd = endOfMonth(restOfMonthStartDate);
    sections.push({
      id: `${upcomingRestOfMonthPrefix}${formatDateKey(romStart)}`,
      startDate: romStart,
      endDate: romEnd,
      isDay: false,
    });
  }

  const monthCounter =
    shouldHaveRestOfMonth && !isSameMonth(restOfMonthStartDate, today) ? 1 : 0;

  const monthSections = Array.from({ length: 10 }, (_, i) => {
    const monthDate = addMonths(today, monthCounter + i + 1);
    const mStart = startOfMonth(monthDate);
    const mEnd = endOfMonth(monthDate);
    return { mStart, mEnd };
  });

  monthSections.every(({ mStart, mEnd }) => {
    if (isAfter(mStart, maxDate)) {
      return false;
    }

    sections.push({
      id: `${upcomingMonthPrefix}${formatDateKey(mStart)}`,
      startDate: mStart,
      endDate: mEnd,
      isDay: false,
    });

    return true;
  });

  const yearDate = addMonths(today, monthCounter + 11);
  const yearStart = startOfMonth(yearDate);
  const yearEnd = endOfYear(yearDate);

  if (!isAfter(yearStart, maxDate)) {
    sections.push({
      id: `${upcomingYearPrefix}${formatDateKey(yearStart)}`,
      startDate: yearStart,
      endDate: yearEnd,
      isDay: false,
    });
  }

  return sections;
};

const findSectionForDate = (
  date: Timestamp,
  sections: UpcomingSection[],
): UpcomingSection | undefined => {
  if (isBefore(date, sections[1].startDate)) {
    return sections[0];
  }

  return sections
    .slice(1)
    .find(
      (section) =>
        !isBefore(date, section.startDate) && !isAfter(date, section.endDate),
    );
};

export const buildUpcomingDeadlineBuckets = ({
  tasks,
  today,
  weekStartsOn,
  dateFilterEndDate,
  shouldHaveRestOfMonth,
  restOfMonthStartDate,
  showCompleted = false,
  durationFilters,
}: BuildUpcomingDeadlineBucketsOptions): UpcomingDeadlineBuckets => {
  const maxDate = dateFilterEndDate ?? endOfYear(addMonths(today, 12));
  const uncompletedOnly = !showCompleted;

  const occurrenceMatchesDuration = (occurrence: {
    timeslot?: { duration?: number | null } | null;
  }): boolean =>
    someFilterMatchesDuration(occurrence.timeslot?.duration, durationFilters);
  const sections = buildSections(
    today,
    shouldHaveRestOfMonth,
    restOfMonthStartDate,
    maxDate,
  );

  const findPerSectionNonDayDate = (task: Task): Timestamp | undefined => {
    for (const section of sections) {
      if (section.isDay) {
        continue;
      }

      const sectionEndDate =
        dateFilterEndDate && isBefore(dateFilterEndDate, section.endDate)
          ? dateFilterEndDate
          : section.endDate;

      if (isBefore(sectionEndDate, section.startDate)) {
        return undefined;
      }

      const sectionOccurrences = getTaskPlanning(task, {
        startDate: section.startDate,
        endDate: sectionEndDate,
        today,
        weekStartsOn,
        uncompletedOnly,
      });

      const matchingOccurrence = sectionOccurrences.find(
        occurrenceMatchesDuration,
      );
      if (matchingOccurrence) {
        return matchingOccurrence.date;
      }
    }

    return undefined;
  };

  const overdueDeadline = endOfDay(addDays(today, -1));

  const sectionTaskIds: Record<ID, ID[]> = {};
  const sectionTasks: Record<ID, Task[]> = {};
  sections.forEach((section) => {
    sectionTaskIds[section.id] = [];
    sectionTasks[section.id] = [];
  });

  const usedInNonDaySection = new Set<ID>();

  tasks.forEach((task) => {
    const effectiveDeadline = getEffectiveDeadline(task);

    const overduePlanning = getDueTaskPlanning(task, {
      date: overdueDeadline,
      today,
      weekStartsOn,
    });

    const duePlanning = overduePlanning.filter(
      (plannedOccurrence) =>
        !isAfter(plannedOccurrence.date, overdueDeadline) &&
        occurrenceMatchesDuration(plannedOccurrence),
    );

    const plannedOccurrence = duePlanning[duePlanning.length - 1];
    const isOverdue =
      !!plannedOccurrence ||
      (!!effectiveDeadline && !isAfter(effectiveDeadline, overdueDeadline));

    if (isOverdue && !task.completedAt) {
      sectionTaskIds[overdueSectionID].push(task.id);
      sectionTasks[overdueSectionID].push(task);
    }

    const taskDates = new Set<string>();

    if (effectiveDeadline && !isBefore(effectiveDeadline, today)) {
      const shouldSkip =
        dateFilterEndDate &&
        isAfter(effectiveDeadline, dateFilterEndDate) &&
        task.type !== TaskType.Task;

      if (!shouldSkip) {
        taskDates.add(effectiveDeadline.toISOString());
      }
    }

    if (!effectiveDeadline && task.schedules?.length) {
      // Day sections are contiguous (today..today+9); merging them into one
      // call avoids re-parsing rrules per day. Critical: rrule re-parse cost
      // dominates per-task time.
      const daySectionsEndDate = endOfDay(addDays(today, 9));
      const dayOccurrences = getTaskPlanning(task, {
        startDate: startOfDay(today),
        endDate: daySectionsEndDate,
        today,
        weekStartsOn,
        uncompletedOnly,
      });
      dayOccurrences.forEach((dayOccurrence) => {
        const { date } = dayOccurrence;
        if (dateFilterEndDate && isAfter(date, dateFilterEndDate)) {
          return;
        }
        if (!occurrenceMatchesDuration(dayOccurrence)) {
          return;
        }
        taskDates.add(date.toISOString());
      });

      const nonDayDate = findPerSectionNonDayDate(task);

      nonDayDate && taskDates.add(nonDayDate.toISOString());
    }

    taskDates.forEach((dateIso) => {
      const date = new Date(dateIso);
      const section = findSectionForDate(date, sections);

      if (!section) {
        return;
      }

      if (!section.isDay) {
        if (usedInNonDaySection.has(task.id)) {
          return;
        }
        usedInNonDaySection.add(task.id);
      }

      if (!sectionTaskIds[section.id].includes(task.id)) {
        sectionTaskIds[section.id].push(task.id);
        sectionTasks[section.id].push(task);
      }
    });
  });

  return { sections, sectionTasks };
};
